'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Button } from 'foundations-components/transitional/components/Button';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { View } from 'foundations-components/components/View';
import { Flex } from 'foundations-components/components/Flex';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { useQuery } from 'data-fetching-client';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { useScrollEventListener } from 'marketplace-ui-common/hooks/useScrollEventListener';
import { useScrollToTop } from 'marketplace-ui-common/hooks/useScrollToTop';
import { getIsPrerenderUserAgent } from 'marketplace-ui-common/utils/userAgent';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { getDataFetchingClientRequestState } from 'marketplace-ui-common/dataFetchingClient/utils/getDataFetchingClientRequestState';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import MainSection from 'marketplace-ui-common/layout/components/MainSection';
import MainSectionInner from 'marketplace-ui-common/layout/components/MainSectionInner';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import { GET_REVIEWS_SUMMARY } from 'marketplace-ui-reviews/queries/getReviewsSummaryQuery';
import { useReviewFilters } from 'marketplace-ui-reviews/hooks/useReviewFilters';
import { useUsageTracker } from 'marketplace-ui-tracking';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { clearProfileListing, getProfileListing } from '../actions/ProfileActions';
import GenericError from '../components/error/GenericError';
import AboutCard from '../components/profile/AboutCard';
import ContactInfoCard from '../components/profile/ContactInfoCard';
import GeneralInfoCard from '../components/profile/GeneralInfoCard';
import ProfileHeader from '../components/profile/ProfileHeader';
import ProfilePageMeta from '../components/profile/ProfilePageMeta';
import ReviewsCard from '../components/profile/ReviewsCard';
import UsageViewTracker from '../components/tracking/UsageViewTracker';
import { CARD_PADDING, LOGO_WRAPPER_OFFSET, PROFILE_LEFT_COL_SIZE, PROFILE_RIGHT_COL_SIZE } from '../constants/ProfileConstants';
import { getAccreditations } from '../selectors/accreditations';
import { getAppContextCoreType, getCanonicalAppContext, getIsPublicApp } from '../selectors/app';
import { getCertificationsRequestState } from '../selectors/certifications';
import { getProfileListingDetails, getProfileListingRequestError, getProfileListingRequestState } from '../selectors/profile';
import { getIsProfileListingAccredited } from '../utils/credentials';
import { addViewedProfile } from '../utils/homepage';
import { getProfilePageTitleText } from '../utils/i18n';
import { getInAppReviewLink } from '../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ProfileLoadError = () => /*#__PURE__*/_jsxs(NavMarker, {
  name: "PROFILE_404_LOAD",
  children: [/*#__PURE__*/_jsx(DocumentHead, {
    children: /*#__PURE__*/_jsx(PrerenderReady, {
      statusCode: 404
    })
  }), /*#__PURE__*/_jsx(ErrorMessage, {
    type: "notFound"
  }), /*#__PURE__*/_jsx(TFlex, {
    justify: "center",
    children: /*#__PURE__*/_jsx(UIRouterLink, {
      use: "unstyled",
      to: "/",
      children: /*#__PURE__*/_jsx(Button, {
        use: "tertiary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystem-marketplace-solutions-ui.profile.back"
        })
      })
    })
  })]
});
const ProfileLoadingSpinner = () => /*#__PURE__*/_jsx("div", {
  style: {
    width: '100vw'
  },
  children: /*#__PURE__*/_jsx(Flex, {
    paddingBlockStart: "1500",
    justifySelf: "center",
    children: /*#__PURE__*/_jsx(LoadingSpinner, {})
  })
});
const Profile = ({
  location: {
    pathname,
    search
  },
  match: {
    params: {
      profileSlug: slug
    }
  }
}) => {
  useScrollToTop(pathname);
  const tracker = useUsageTracker();
  const appContext = useSelector(getAppContextCoreType);
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const isPublicApp = useSelector(getIsPublicApp);
  const isReviewLink = pathname.includes('/write-review');
  const isPreview = pathname === '/_preview';
  const isPrerender = getIsPrerenderUserAgent();
  const reviewButtonRef = useRef(null);
  const profileListingRequestError = useSelector(getProfileListingRequestError);
  const profileListingRequestState = useSelector(getProfileListingRequestState);
  const profileListing = useSelector(getProfileListingDetails);
  const accreditations = useSelector(getAccreditations);
  const isProfileAccredited = getIsProfileListingAccredited(accreditations, profileListing);
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const {
    reviewFilters,
    updateReviewFilters
  } = useReviewFilters({
    entityId: profileListing.id,
    reviewType: 'PROFILE'
  });
  const prevProfileId = usePrevious(profileListing.id);
  useEffect(() => {
    if (profileListing.id !== prevProfileId) {
      updateReviewFilters(reviewFilters.updateFilters({
        entityId: profileListing.id
      }));
    }
  }, [prevProfileId, profileListing.id, reviewFilters, updateReviewFilters]);
  const {
    reviewsFetchStatus
  } = useReviews({
    appContext,
    reviewFilters,
    offeringId: profileListing.id,
    reviewType: 'PROFILE'
  });
  const [reviewSummary, setReviewSummary] = useState(undefined);
  const [reviewSummaryRequestState, setReviewSummaryRequestState] = useState('UNINITIALIZED');
  const {
    data: reviewSummaryData,
    loading: reviewSummaryLoading,
    error: reviewSummaryError
  } = useQuery(GET_REVIEWS_SUMMARY, {
    variables: {
      reviewType: 'PROFILE',
      entityId: profileListing.id
    }
  });
  useEffect(() => {
    setReviewSummaryRequestState(getDataFetchingClientRequestState(reviewSummaryError, reviewSummaryLoading));
    if (reviewSummaryData) {
      setReviewSummary(new Map(Object.entries(reviewSummaryData.reviewSummaryResponse.reviewSummaries[0].reviewSummary)));
    }
  }, [reviewSummaryData, reviewSummaryError, reviewSummaryLoading]);
  const dispatch = useDispatch();
  const tabIds = ['about', 'reviews'];
  const scrollOffset = LOGO_WRAPPER_OFFSET + CARD_PADDING;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(tabIds, scrollOffset);
  useScrollEventListener(calculateActiveTab);
  useEffect(() => () => dispatch(clearProfileListing()), [dispatch]);
  useEffect(() => {
    addViewedProfile(profileListing.name);
  }, [profileListing.name]);
  const getUsageEventProperties = useCallback((extras = {}) => isProfileAccredited ? Object.assign({}, extras, {
    screen: 'profile',
    tags: ['accredited']
  }) : Object.assign({}, extras, {
    screen: 'profile'
  }), [isProfileAccredited]);
  const handleTrackInteraction = useCallback((action, extras = {}, trackFn = tracker.track) => {
    trackFn('profile-interaction', getUsageEventProperties(Object.assign({
      action
    }, extras)));
  }, [tracker, getUsageEventProperties]);
  const handleScrollToTabSection = useCallback(tabSection => {
    scrollToTabSection(tabSection);
    if (tabSection === 'reviews') {
      reviewButtonRef.current.focus();
    }
  }, [reviewButtonRef, scrollToTabSection]);
  const renderedGeneralInfoCard = useMemo(() => {
    if (!profileListing) {
      return null;
    }
    return /*#__PURE__*/_jsx(GeneralInfoCard, {
      profileListing: profileListing
    });
  }, [profileListing]);
  useEffect(() => {
    if (isPublicApp && isReviewLink) {
      window.location.href = getInAppReviewLink(slug, search);
    }
  }, [isPublicApp, isReviewLink, slug, search]);
  useEffect(() => {
    dispatch(getProfileListing(slug, isPreview));
  }, [dispatch, isPreview, slug]);
  useEffect(() => {
    if (isSucceeded(profileListingRequestState)) {
      document.title = getProfilePageTitleText(profileListing, I18n.text);
    }
  }, [profileListing, profileListingRequestState]);
  if (profileListingRequestError) {
    const {
      status: statusCode
    } = profileListingRequestError;
    if (statusCode === 404) {
      return /*#__PURE__*/_jsx(ProfileLoadError, {});
    } else {
      return /*#__PURE__*/_jsxs(NavMarker, {
        name: "PROFILE_ERROR_LOAD",
        children: [/*#__PURE__*/_jsx(DocumentHead, {
          children: /*#__PURE__*/_jsx(PrerenderReady, {
            statusCode: statusCode
          })
        }), /*#__PURE__*/_jsx(GenericError, {})]
      });
    }
  }
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(ProfilePageMeta, {
      canonicalAppContext: canonicalAppContext,
      certificationsRequestState: certificationsRequestState,
      pathname: pathname,
      profileListing: profileListing,
      profileListingRequestState: profileListingRequestState,
      profileReviewsRequestState: reviewsFetchStatus,
      profileReviewSummaryRequestState: reviewSummaryRequestState
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      LoadingComponent: ProfileLoadingSpinner,
      ErrorComponent: ProfileLoadError,
      requestStatus: profileListingRequestState,
      children: /*#__PURE__*/_jsxs(NavMarker, {
        name: "PROFILE_LOAD",
        children: [/*#__PURE__*/_jsxs(View, {
          backgroundColor: "neutral-100",
          children: [isPreview && /*#__PURE__*/_jsx(Alert, {
            titleText: unescapedText('ecosystem-marketplace-solutions-ui.profile.previewAlert.titleText'),
            type: "warning",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "ecosystem-marketplace-solutions-ui.profile.previewAlert.message"
            })
          }), /*#__PURE__*/_jsx(ProfileHeader, {
            activeTab: activeTab,
            canonicalAppContext: canonicalAppContext,
            isPrerender: isPrerender,
            isPreview: isPreview,
            onTrackInteraction: handleTrackInteraction,
            profileListing: profileListing,
            scrollToTabSection: handleScrollToTabSection
          }), /*#__PURE__*/_jsx(MainSection, {
            paddingBlockStart: "0",
            color: "light",
            responsive: true,
            children: /*#__PURE__*/_jsx(MainSectionInner, {
              children: /*#__PURE__*/_jsxs(Grid, {
                "data-test-id": "details-page-container",
                children: [/*#__PURE__*/_jsxs(GridItem, {
                  size: PROFILE_LEFT_COL_SIZE,
                  children: [/*#__PURE__*/_jsx(ContactInfoCard, {
                    onTrackInteraction: handleTrackInteraction,
                    profileListing: profileListing,
                    scrollToTabSection: handleScrollToTabSection
                  }), /*#__PURE__*/_jsx(MediaQueryView, {
                    min: ScreenSizes.MD,
                    children: renderedGeneralInfoCard
                  })]
                }), /*#__PURE__*/_jsxs(GridItem, {
                  size: PROFILE_RIGHT_COL_SIZE,
                  children: [/*#__PURE__*/_jsx(AboutCard, {
                    profileListing: profileListing
                  }), /*#__PURE__*/_jsx(ReviewsCard, {
                    isPreview: isPreview,
                    onFiltersUpdate: updateReviewFilters,
                    onTrackInteraction: handleTrackInteraction,
                    profileListing: profileListing,
                    reviewButtonRef: reviewButtonRef,
                    reviewFilters: reviewFilters,
                    reviewSummary: reviewSummary,
                    scrollToTabSection: handleScrollToTabSection
                  }), /*#__PURE__*/_jsx(MediaQueryView, {
                    max: ScreenSizes.MD,
                    children: renderedGeneralInfoCard
                  })]
                })]
              })
            })
          })]
        }), /*#__PURE__*/_jsx(UsageViewTracker, {
          name: "profile-pageview",
          properties: getUsageEventProperties
        })]
      })
    })]
  });
};
Profile.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      profileSlug: PropTypes.string
    }).isRequired
  }).isRequired
};
export default Profile;